import type { definitions } from '@luxuryescapes/contract-svc-agent'
import { allCountries } from 'country-telephone-data'

type ServerAgentAccount = definitions['userSimpleSchema'] | definitions['meSchema']
type ServerAgencyDetails = definitions['userWithAgencyResponse']
type ServerAgentEmailStatus = definitions['userStatusResponse']
type ServerAgentEmailStatusWithDetails = ServerAgentEmailStatus & {
  country_code?: string
  countryCode?: string
  region?: string
  title?: App.Title
}

const MIN_LOCAL_PHONE_LENGTH = 7
const dialCodesByLength = Array.from(new Set(allCountries.map(country => country.dialCode))).sort((a, b) => b.length - a.length)

function mapAgentStatusPhone(response: ServerAgentEmailStatus): Pick<App.AgentHubAgentStatusData, 'phone' | 'phonePrefix'> {
  if (response.phone) {
    return {
      phone: response.phone,
      ...(response.phone_prefix && { phonePrefix: response.phone_prefix }),
    }
  }

  if (!response.phone_prefix) {
    return {}
  }

  const numericPhonePrefix = response.phone_prefix.replace(/\D/g, '')
  const dialCode = dialCodesByLength.find(dialCode => (
    numericPhonePrefix.startsWith(dialCode) &&
    numericPhonePrefix.length - dialCode.length >= MIN_LOCAL_PHONE_LENGTH
  ))

  if (!dialCode) {
    return {
      phonePrefix: response.phone_prefix,
    }
  }

  // svc-agent can return the full phone number in phone_prefix with phone omitted.
  // Split it here so PhoneInput receives the separate dial code and local number it expects.
  return {
    phonePrefix: dialCode,
    phone: numericPhonePrefix.slice(dialCode.length),
  }
}

export function agentHubCheckEmailStatusMapper(response: ServerAgentEmailStatus): App.AgentHubAgentStatusData {
  const responseWithDetails = response as ServerAgentEmailStatusWithDetails

  return {
    status: response.status ?? '',
    agentId: response.agent_id ?? '',
    action: response.action,
    customerId: response.customer_id,
    ...(response.email && { email: response.email }),
    ...(response.firstName && { firstName: response.firstName }),
    ...(response.surname && { surname: response.surname }),
    ...mapAgentStatusPhone(response),
    ...(responseWithDetails.title && { title: responseWithDetails.title }),
    ...((responseWithDetails.country_code || responseWithDetails.countryCode || responseWithDetails.region) && {
      countryCode: responseWithDetails.country_code ?? responseWithDetails.countryCode ?? responseWithDetails.region,
    }),
  }
}

export function agentHubAccountMapper(response: ServerAgentAccount): App.AgentHubAccount {
  return {
    agentId: response.id,
    name: response.name ?? '',
    phone: response.phone ?? '',
    email: response.email ?? '',
    affiliation: response.affiliation,
    affiliationId: response.affiliationId ?? '',
    agencyId: response.agencyId ?? '',
    agencyName: response.agencyName ?? '',
    status: response.status as 'onboarded' | 'pending',
    agencyLogoId: response.agencyLogoId,
    agentTier: response.tier || 'tier1',
    hasAgencyIdentifier: ('hasAgencyIdentifier' in response) ? response.hasAgencyIdentifier : false,
    agentHasPayoutAccountSetup: ('hp' in response) ? response.hp : false,
    customerId: response.customerId || '',
    affiliationRules: 'affiliationRules' in response
      ? response.affiliationRules
      : undefined,
    societeMemberNumber: response.societeMemberNumber,
    societeRequestsInteractions: response.societeRequestsInteractions,
  }
}

export function agentHubAgencyMapper(response: ServerAgencyDetails): App.AgentHubAgency {
  return {
    id: response.id,
    name: response.name ?? '',
    agencyIdentifier: response.businessCodeValue,
    agencyIdentifierType: response.businessCodeType,
    address: response.address,
    city: response.city,
    state: response.state,
    postcode: response.postcode,
    country: response.country,
    affiliation: response.affiliation,
    brand: response.brand,
  }
}

export function mapBedbankPackageToAgentHubPayload(rate: App.BedbankRate) {
  return { packageId: rate.id, mx: rate.mx || '' }
}

export function mapLPCPackageToAgentHubPayload(pkg: App.HotelPackage) {
  return {
    packageId: pkg.id,
    mx: pkg.roomRate.mx!,
  }
}

export function mapFlashPackageToAgentHubPayload(pkg: App.HotelPackage) {
  return {
    packageId: pkg.id,
    mx: pkg.mx!,
  }
}
